// apps/client/src/modules/Shortcuts/ShortcutRegistry.ts
import { atom } from 'jotai'

export type ShortcutGroup = 'tabs' | 'editor' | 'notebook' | 'general'

export type ShortcutKey = {
  cmd?: boolean
  shift?: boolean
  alt?: boolean
  key: string
}

export type ShortcutDefinition = {
  id: string
  keys: ShortcutKey | ShortcutKey[]
  group: ShortcutGroup
  labelKey: string
  firesInEditor?: boolean
  displayOnly?: boolean
  handler?: (e: KeyboardEvent) => void
}

export const shortcutRegistryAtom =
  atom<Record<string, ShortcutDefinition>>({})

export const IS_MAC: boolean =
  typeof navigator !== 'undefined' &&
  (
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    ''
  ).toUpperCase().includes('MAC')

const normalizeKey = (key: string): string => {
  if (key.length === 1) return key.toUpperCase()
  return key
}

// Mac Option+letter/digit/punctuation produces dead-key characters in e.key (⌥1=¡, ⌥A=å, ⌥/=÷).
// Recover the literal character from e.code so bindings like { alt: true, key: '/' } still match.
const PUNCT_CODE_TO_KEY: Record<string, string> = {
  Slash: '/',
  Period: '.',
  Comma: ',',
  Minus: '-',
  Equal: '=',
  Backslash: '\\',
  Quote: "'",
  Semicolon: ';',
  BracketLeft: '[',
  BracketRight: ']',
  Backquote: '`',
}

const macAltLiteral = (e: KeyboardEvent): string | null => {
  if (e.code.startsWith('Digit')) return e.code.slice(5)
  if (e.code.startsWith('Key')) return e.code.slice(3)
  return PUNCT_CODE_TO_KEY[e.code] ?? null
}

const normalizeEvent = (e: KeyboardEvent, isMac: boolean): ShortcutKey => {
  const rawKey = isMac && e.altKey ? macAltLiteral(e) ?? e.key : e.key
  return {
    cmd: isMac ? e.metaKey : e.ctrlKey,
    shift: e.shiftKey,
    alt: e.altKey,
    key: normalizeKey(rawKey),
  }
}

const keyMatches = (binding: ShortcutKey, eventKey: ShortcutKey): boolean =>
  !!binding.cmd === !!eventKey.cmd &&
  !!binding.shift === !!eventKey.shift &&
  !!binding.alt === !!eventKey.alt &&
  normalizeKey(binding.key) === eventKey.key

export const eventMatchesShortcut = (
  e: KeyboardEvent,
  def: ShortcutDefinition,
  isMacOverride: boolean = IS_MAC,
): boolean => {
  const normalized = normalizeEvent(e, isMacOverride)
  const bindings = Array.isArray(def.keys) ? def.keys : [def.keys]
  return bindings.some((binding) => keyMatches(binding, normalized))
}

export const modifierSetMatches = (
  binding: ShortcutKey,
  held: { cmd: boolean; shift: boolean; alt: boolean },
): boolean =>
  !!binding.cmd === held.cmd &&
  !!binding.shift === held.shift &&
  !!binding.alt === held.alt
